import React from 'react'

import { DEFAULT_COLORS } from 'public/components/default_template/meta/StyleMeta'
import { useOptionalThemeColorScheme } from 'public/components/default_template/meta/useTheme'
import { useOptionalRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import UserIcon from './UserIcon'

type UserAvatarProps = {
  firstName?: string | null
  lastName?: string | null
  useThemeColors?: boolean
}

const getFirstChar = (str: string): string | null => {
  return str.length > 0 ? str.charAt(0) : null
}

const getInitials = (
  firstName?: string | null,
  lastName?: string | null
): string | null => {
  const firstInitial = getFirstChar(firstName?.trim() || '')
  const lastInitial = getFirstChar(lastName?.trim() || '')

  if (firstInitial || lastInitial) {
    return `${firstInitial ?? ''}${lastInitial ?? ''}`.toUpperCase()
  }
  return null
}

const UserAvatar = ({
  firstName,
  lastName,
  useThemeColors
}: UserAvatarProps) => {
  const initials = getInitials(firstName, lastName)
  const getColorFromTheme = useOptionalThemeColorScheme()
  const restaurant = useOptionalRestaurant()?.restaurant

  // Get colors from theme using the theme consumer to ensure consistent resolution
  const [initialsColor, border, backgroundColor] = getColorFromTheme(
    (theme) => {
      return [
        theme.colorOverrides?.navigation?.text ?? theme.colorScheme.text.action,
        theme.colorScheme.border.default ?? theme.colorScheme.text.action,
        theme.colorOverrides?.navigation?.background ??
          theme.colorScheme.surface.default
      ]
    },
    [
      restaurant?.meta?.textColor ?? DEFAULT_COLORS.text,
      restaurant?.meta?.primaryColor ?? DEFAULT_COLORS.primary,
      restaurant?.meta?.backgroundColor ?? DEFAULT_COLORS.background
    ]
  )

  return (
    <div
      className='userAvatar'
      aria-label={'User avatar'}
      style={{ backgroundColor }}
    >
      {initials ? (
        <span
          className='userAvatarInitials'
          style={
            useThemeColors
              ? {
                  color: initialsColor,
                  border: `1px solid ${border}`,
                  backgroundColor
                }
              : undefined
          }
        >
          {initials}
        </span>
      ) : (
        <UserIcon color='currentColor' />
      )}
    </div>
  )
}

export default UserAvatar
